import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { base64ToBlob } from 'wflow-cdk';
import { FilePreview, FilePreviewModule, WfBigIconComponent } from 'wflow-ui';
import { findPeppolUblFieldSet } from '../../../../const/peppol-ubl-field-sets.const';
import {
  PeppolAttachment,
  PeppolDetail,
} from '../../../../interfaces/peppol-detail.interface';
import { PeppolUblPreview } from '../../../../interfaces/peppol-ubl-preview.interface';
import { isPeppolTaxDocument } from '../../../../utils/peppol-document-type.util';
import { PEPPOL_XML_CONTENT_TYPE } from '../../peppol-detail-dialog.const';
import { PeppolUblPreviewComponent } from '../peppol-ubl-preview/peppol-ubl-preview.component';

@Component({
  selector: 'wf-peppol-detail-preview',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    FilePreviewModule,
    WfBigIconComponent,
    PeppolUblPreviewComponent,
  ],
  templateUrl: './peppol-detail-preview.component.html',
  styleUrl: './peppol-detail-preview.component.scss',
  host: { class: 'wf-peppol-detail-preview' },
})
export class PeppolDetailPreviewComponent {
  readonly detail = input.required<PeppolDetail>();
  readonly ublPreview = input.required<PeppolUblPreview | null>();
  readonly activeAttachment = input.required<PeppolAttachment | null>();

  protected readonly activeUblPreview = computed<PeppolUblPreview | null>(() =>
    this.resolveUblPreview(
      this.activeAttachment(),
      this.ublPreview(),
      this.detail(),
    ),
  );

  protected readonly showUnavailablePreview = computed<boolean>(
    () =>
      this.activeAttachment()?.contentType === PEPPOL_XML_CONTENT_TYPE &&
      !this.activeUblPreview(),
  );

  protected readonly filePreviewSource = computed<FilePreview | undefined>(() =>
    this.toFilePreviewSource(this.activeAttachment()),
  );

  private resolveUblPreview(
    activeAttachment: PeppolAttachment | null,
    ublPreview: PeppolUblPreview | null,
    detail: PeppolDetail,
  ): PeppolUblPreview | null {
    if (activeAttachment?.contentType !== PEPPOL_XML_CONTENT_TYPE) {
      return null;
    }
    if (!isPeppolTaxDocument(detail.documentType)) {
      return null;
    }
    return ublPreview != null &&
      findPeppolUblFieldSet(detail.documentType) != null
      ? ublPreview
      : null;
  }

  private toFilePreviewSource(
    attachment: PeppolAttachment | null,
  ): FilePreview | undefined {
    if (
      !attachment ||
      attachment.contentType === PEPPOL_XML_CONTENT_TYPE ||
      !attachment.content
    ) {
      return undefined;
    }
    return {
      id: attachment.name,
      name: attachment.name,
      contentType: attachment.contentType,
      contentExist: true,
      blob: base64ToBlob(attachment.content, attachment.contentType),
    };
  }
}
